import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import Modal from '../../Modal';
import Heading from '../../Heading';
import ProductCard from '../../ProductCard';

import styles from './PromocodeGoodsModal.styl';
import useNewStockGoods from '../../../scenes/Home/StockGoods/useNewStockGoods';
import ProductCardCompact from '../../ProductCardCompact';

const cx = classNames.bind(styles);

export const PromocodeGoodsModal = props => {
  const { className, isOpen, onModalClose, initialNodeId, isReplace } = props;
  const intl = useIntl();
  const isPhone = useSelector(state => state.responsive.isPhone || false);

  const {
    nodes,
    currentNode,
    setCurrentById,
    filtredGoods,
    removeIngredientAction,
    cancelRemovedIngredientsAction,
    addToCart,
    type,
  } = useNewStockGoods({ initialNodeId, isReplace, onModalClose });

  const nodesForUi = useMemo(() => {
    if (!isReplace) return nodes;

    const hasInitial = initialNodeId && nodes.some(n => n.id === initialNodeId);
    if (!hasInitial) return nodes;

    return nodes.map(n => ({
      ...n,
      disabled: n.id !== initialNodeId,
    }));
  }, [nodes, isReplace, initialNodeId]);

  if (!isOpen) return null;

  if (!currentNode) return null;
  const headerTitle = intl.formatMessage({ id: 'field.promocode.title' });
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const autoSwitchedRef = useRef(new Set());

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!currentNode) return;
    if (currentNode.kind !== 'condition') return;

    const applied = Number(currentNode.applied_count || 0);
    const total = Number(currentNode.totalCount || 0);

    if (!total || applied < total) return;

    // Уже автопереключали с этой ноды? Тогда НЕ мешаем юзеру возвращаться назад.
    if (autoSwitchedRef.current.has(currentNode.id)) return;

    const currentIndex = nodesForUi.findIndex(n => n.id === currentNode.id);
    if (currentIndex === -1) return;

    const nextNode = nodesForUi.slice(currentIndex + 1).find(n => !n.disabled);
    if (!nextNode) return;

    autoSwitchedRef.current.add(currentNode.id);
    setCurrentById(nextNode.id);
  }, [currentNode, nodesForUi, setCurrentById]);

  return (
    <Modal className={cx('PromocodeGoodsModal', className)} isOpen={isOpen} onClose={onModalClose}>
      <div className={cx('PromocodeGoodsModal__header')}>
        <Heading className={cx('PromocodeGoodsModal__heading')}>
          {isReplace && intl.formatMessage({ id: 'field.promocode.replace' })}
          {!isReplace && headerTitle}
        </Heading>

        <button
          type="button"
          className={cx('PromocodeGoodsModal__close')}
          onClick={onModalClose}
          aria-label={intl.formatMessage({ id: 'button.close' })}
        >
          ×
        </button>

        <div className={cx('PromocodeGoodsModal__radios')}>
          {nodesForUi.map(node => {
            const currentAppliedCount = Number(node.applied_count || 0);
            const isActive = currentNode?.id === node.id;
            const currentAppliedCount2 =
              currentAppliedCount >= Number(node.totalCount)
                ? Number(node.totalCount)
                : currentAppliedCount;
            return (
              // eslint-disable-next-line jsx-a11y/label-has-associated-control
              <label
                key={node.id}
                className={cx('PromocodeGoodsModal__radio', {
                  PromocodeGoodsModal__radio_active: node.id === currentNode.id,
                  PromocodeGoodsModal__radio_disabled: node.disabled,
                })}
              >
                <input
                  type="radio"
                  className={cx('PromocodeGoodsModal__radio-input')}
                  value={node.id}
                  disabled={node.disabled}
                  checked={node.id === currentNode.id}
                  onChange={() => setCurrentById(node.id)}
                />
                {/* <span className={cx('PromocodeGoodsModal__radio-label')}>{node.label}</span> */}
                <div className={cx('PromocodeGoodsModal__radio-content')}>
                  <span className={cx('PromocodeGoodsModal__radio-label')}>{node.label}</span>

                  {node.kind === 'condition' && Number(node.totalCount) > 0 && (
                    <span className={cx('PromocodeGoodsModal__radio-meta')}>
                      <span
                        className={cx('PromocodeGoodsModal__radio-count')}
                        style={{
                          color: isActive ? '#ffffff' : '#50A684',
                        }}
                      >
                        {currentAppliedCount2}/{Number(node.totalCount)}
                      </span>

                      <span
                        className={cx('PromocodeGoodsModal__progress')}
                        style={{
                          backgroundColor: isActive ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.08)',
                        }}
                        aria-hidden="true"
                      >
                        <span
                          className={cx('PromocodeGoodsModal__progressBar')}
                          style={{
                            width: `${Math.min(
                              (Number(node.applied_count || 0) / Number(node.totalCount)) * 100,
                              100,
                            )}%`,
                            backgroundColor: isActive ? '#ffffff' : '#50A684',
                          }}
                        />
                      </span>
                    </span>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className={cx('PromocodeGoodsModal__list')}>
        {filtredGoods.map(good => {
          if (!isPhone) {
            return (
              <ProductCard
                className={cx('PromocodeGoodsModal__item')}
                key={good.alias}
                good={good}
                stockRole={currentNode.stockRole}
                removeIngredientAction={removeIngredientAction}
                cancelRemovedIngredientsAction={cancelRemovedIngredientsAction}
                addToCart={addToCart}
                type={type}
                isReward
                rewardIndex={currentNode?.index}
              />
            );
          }
          return (
            <ProductCardCompact
              className={cx('PromocodeGoodsModal__item')}
              key={good.alias}
              good={good}
              stockRole={currentNode.stockRole}
              removeIngredientAction={removeIngredientAction}
              cancelRemovedIngredientsAction={cancelRemovedIngredientsAction}
              addToCart={addToCart}
              type={type}
              isReward
              rewardIndex={currentNode?.index}
            />
          );
        })}
      </div>
    </Modal>
  );
};

PromocodeGoodsModal.defaultProps = {
  className: '',
  initialNodeId: null,
  isReplace: false,
};

PromocodeGoodsModal.propTypes = {
  className: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  initialNodeId: PropTypes.string,
  isReplace: PropTypes.bool,
};
